import React, { useState, useEffect, useRef } from 'react';
import { getChatAngebotRequest, submitChatAngebotRequest } from '../api/client';
import { ChevronRight, Receipt } from './icons';

// Полноэкранное окно заявки на доступ к Chat-Angebot.de (открывается кнопкой
// «Попробовать Chat-Angebot» на главной). Подтягивает данные, заполненные в воронке
// (имя/телефон/email/фирма/регион), даёт их отредактировать и подтвердить отправку.
// Ответ клиенту обещаем в течение 24 часов.

const CA_CSS = `
.ca-overlay { position: fixed; inset: 0; z-index: 1000; background: var(--bg-primary); overflow-y: auto; -webkit-overflow-scrolling: touch; }
.ca-anim { animation: fadeInUp .3s cubic-bezier(.22,.68,0,1) both; }
.ca-input { width: 100%; padding: 13px 14px; border-radius: 12px; border: 1px solid var(--border); background: var(--bg-card); color: var(--text-primary); font-size: 15px; outline: none; transition: border-color .2s ease; }
.ca-input:focus { border-color: var(--accent); }
.ca-input::placeholder { color: var(--text-tertiary); }
@media (prefers-reduced-motion: reduce) { .ca-anim { animation: none !important; } }
`;

const FIELDS = [
  { key: 'first_name', label: 'Имя', placeholder: 'Иван', type: 'text', required: true },
  { key: 'last_name', label: 'Фамилия', placeholder: 'Иванов', type: 'text' },
  { key: 'phone', label: 'Телефон', placeholder: '+49 151 2345678', type: 'tel' },
  { key: 'email', label: 'E-mail (на него придёт ответ)', placeholder: 'name@mail.de', type: 'email', required: true },
  { key: 'company', label: 'Фирма (необязательно)', placeholder: 'Muster Bau GmbH', type: 'text' },
];

export default function ChatAngebotRequest({ onClose }) {
  const [values, setValues] = useState(null); // null = грузим префилл
  const [region, setRegion] = useState('');
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const backHandledRef = useRef(false);

  useEffect(() => {
    getChatAngebotRequest()
      .then((d) => {
        // Если заявка уже была — префиллим её данными (правки поверх), иначе профилем из воронки.
        const src = d.request || d.profile || {};
        setValues({
          first_name: src.first_name || '',
          last_name: src.last_name || '',
          phone: src.phone || '',
          email: src.email || '',
          company: src.company || '',
        });
        setRegion(src.region || d.profile?.region || '');
        setExisting(d.request || null);
      })
      .catch(() => setValues({ first_name: '', last_name: '', phone: '', email: '', company: '' }));
  }, []);

  // Системная кнопка «Назад» Telegram закрывает окно (плюс своя кнопка в шапке).
  useEffect(() => {
    const bb = window.Telegram?.WebApp?.BackButton;
    if (!bb) return;
    const handler = () => { backHandledRef.current = true; onClose(); };
    bb.onClick(handler);
    bb.show();
    return () => { bb.offClick(handler); bb.hide(); };
  }, [onClose]);

  const set = (key) => (e) => { setValues((v) => ({ ...v, [key]: e.target.value })); setError(''); };

  const submit = async () => {
    if (busy || !values) return;
    if (!values.first_name.trim()) { setError('Укажите имя'); return; }
    if (!values.email.trim()) {
      setError('Укажите e-mail — на него придёт ответ по заявке');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await submitChatAngebotRequest({ ...values, region });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.('success');
      setSent(true);
    } catch (e) {
      setError(e?.message || 'Не получилось отправить. Попробуйте ещё раз.');
    }
    setBusy(false);
  };

  return (
    <div className="ca-overlay">
      <style>{CA_CSS}</style>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '16px 16px calc(24px + var(--safe-bottom))', paddingTop: 'calc(16px + var(--tg-safe-top))' }}>

        {/* Шапка: назад + заголовок */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <button
            onClick={onClose}
            aria-label="Назад"
            style={{
              width: 36, height: 36, borderRadius: 10, border: '1px solid var(--border)',
              background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <span style={{ display: 'flex', transform: 'rotate(180deg)' }}><ChevronRight size={20} /></span>
          </button>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Доступ к Chat-Angebot.de</div>
        </div>

        {sent ? (
          /* Экран успеха */
          <div className="ca-anim" style={{ textAlign: 'center', paddingTop: 40 }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', margin: '0 auto 18px',
              background: 'var(--accent-dim)', color: 'var(--accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Receipt size={34} />
            </div>
            <div style={{ fontSize: 19, fontWeight: 700, marginBottom: 10 }}>Заявка отправлена!</div>
            <div style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 28 }}>
              Мы проверим данные и откроем вам доступ к Chat-Angebot.de.<br />
              Ответ придёт в течение <b style={{ color: 'var(--text-primary)' }}>24 часов</b> на указанную почту.
            </div>
            <button
              onClick={onClose}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 12, border: 'none',
                background: 'var(--accent)', color: '#0D0D0D', fontSize: 15, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Понятно
            </button>
          </div>
        ) : values === null ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>Загрузка…</div>
        ) : (
          <div className="ca-anim">
            <div style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14,
              padding: '13px 14px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 18,
            }}>
              Проверьте данные — мы подставили их из вашей анкеты. С ними уйдёт заявка на доступ
              к сервису смет <b style={{ color: 'var(--text-primary)' }}>Chat-Angebot.de</b>.
              Ответ придёт в течение <b style={{ color: 'var(--accent)' }}>24 часов</b> на почту.
            </div>

            {existing && (
              <div style={{
                background: 'var(--accent-dim)', border: '1px solid rgba(255,204,0,0.3)', borderRadius: 12,
                padding: '11px 14px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.45, marginBottom: 18,
              }}>
                Вы уже отправляли заявку — повторная отправка обновит данные.
              </div>
            )}

            {FIELDS.map((f) => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                  {f.label}{f.required && <span style={{ color: 'var(--accent)' }}> *</span>}
                </div>
                <input
                  className="ca-input"
                  type={f.type}
                  value={values[f.key]}
                  placeholder={f.placeholder}
                  onChange={set(f.key)}
                  autoComplete="off"
                />
              </div>
            ))}

            {error && (
              <div style={{ color: 'var(--error)', fontSize: 13, marginBottom: 12, lineHeight: 1.4 }}>{error}</div>
            )}

            <button
              onClick={submit}
              disabled={busy}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 12, border: 'none', marginTop: 4,
                background: 'var(--accent)', color: '#0D0D0D', fontSize: 15, fontWeight: 700,
                cursor: 'pointer', opacity: busy ? 0.6 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              {busy ? 'Отправляем…' : 'Подтвердить и отправить заявку'}
              {!busy && <ChevronRight size={18} />}
            </button>

            <button
              onClick={onClose}
              style={{
                width: '100%', padding: '13px 16px', borderRadius: 12, marginTop: 10,
                border: '1px solid var(--border)', background: 'transparent',
                color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Назад
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
